import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

export type UiLanguage = 'en' | 'ko';

export const UI_LANGUAGE_STORAGE_KEY = 'ui-language';
export const KOREAN_CATALOG_EXPANSION_PATH = `${import.meta.env.BASE_URL}locales/ko-catalog-expansion.json`;
export const KOREAN_CATALOG_REQUEST_TIMEOUT_MS = 8_000;

type KoreanCatalog = Record<string, string>;

let koreanCatalogExpansionPromise: Promise<KoreanCatalog> | undefined;

const isKoreanCatalog = (value: unknown): value is KoreanCatalog => (
  value !== null
  && typeof value === 'object'
  && !Array.isArray(value)
  && Object.getPrototypeOf(value) === Object.prototype
  && !Object.keys(value).some((key) => key === '__proto__' || key === 'constructor' || key === 'prototype')
  && Object.values(value).every((entry) => typeof entry === 'string')
);

const loadKoreanCatalogExpansion = async (): Promise<KoreanCatalog> => {
  const controller = new AbortController();
  let timeout: ReturnType<typeof setTimeout> | undefined;

  try {
    const timeoutError = new Promise<never>((_, reject) => {
      timeout = setTimeout(() => {
        controller.abort();
        reject(new Error(`Korean catalog request timed out after ${KOREAN_CATALOG_REQUEST_TIMEOUT_MS}ms`));
      }, KOREAN_CATALOG_REQUEST_TIMEOUT_MS);
    });
    const download = async (): Promise<unknown> => {
      const response = await fetch(KOREAN_CATALOG_EXPANSION_PATH, {
        credentials: 'same-origin',
        signal: controller.signal,
      });
      if (!response.ok) {
        throw new Error(`Korean catalog request failed with ${response.status}`);
      }
      return await response.json();
    };
    // Bound the full body download, not just receipt of response headers.
    const catalog: unknown = await Promise.race([download(), timeoutError]);
    if (!isKoreanCatalog(catalog)) {
      throw new Error('Korean catalog must be a plain string:string object without unsafe keys');
    }
    return catalog;
  } finally {
    if (timeout !== undefined) {
      clearTimeout(timeout);
    }
  }
};

export const ensureKoreanCatalog = (): Promise<KoreanCatalog> => {
  if (!koreanCatalogExpansionPromise) {
    koreanCatalogExpansionPromise = loadKoreanCatalogExpansion().then((catalog) => {
      i18n.addResourceBundle('ko', 'translation', catalog, true, false);
      return catalog;
    }).catch((error: unknown) => {
      koreanCatalogExpansionPromise = undefined;
      throw error;
    });
  }
  return koreanCatalogExpansionPromise;
};

export const activateLanguage = async (language: UiLanguage): Promise<boolean> => {
  await i18nInitialization;
  const normalized = normalizeLanguage(language);
  if (normalized === 'ko') {
    try {
      await ensureKoreanCatalog();
    } catch {
      return false;
    }
  }
  await i18n.changeLanguage(normalized);
  return true;
};

const ko = {};

export const normalizeLanguage = (value: unknown): UiLanguage => (
  value === 'ko' ? 'ko' : 'en'
);

export const getStoredLanguage = (): UiLanguage => {
  if (typeof window === 'undefined') {
    return 'en';
  }

  try {
    return normalizeLanguage(window.localStorage.getItem(UI_LANGUAGE_STORAGE_KEY));
  } catch {
    return 'en';
  }
};

export const persistLanguage = (language: UiLanguage) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(UI_LANGUAGE_STORAGE_KEY, language);
  } catch {
    // ignore storage failures
  }
};

export const getLocaleForLanguage = (language: UiLanguage): string => (
  language === 'ko' ? 'ko-KR' : 'en-US'
);

export const getCurrentLocale = (): string => {
  const activeLanguage = normalizeLanguage(i18n.resolvedLanguage || i18n.language || getStoredLanguage());
  return getLocaleForLanguage(activeLanguage);
};

export const i18nInitialization = !i18n.isInitialized
  ? i18n.use(initReactI18next).init({
    resources: {
      ko: { translation: ko },
    },
    lng: 'en',
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false,
    },
    keySeparator: false,
    nsSeparator: false,
    returnNull: false,
  })
  : Promise.resolve();

// Unit tests exercise many independent UI surfaces by calling i18n.changeLanguage
// directly. Their module bootstrap loads the same JSON payload, while production
// keeps this branch dead so the catalog remains an external same-origin asset.
if (import.meta.env.MODE === 'test') {
  const testCatalog = await import('../../../public/locales/ko-catalog-expansion.json');
  i18n.addResourceBundle('ko', 'translation', testCatalog.default, true, false);
  koreanCatalogExpansionPromise = Promise.resolve(testCatalog.default);
}

export const resetKoreanCatalogLoaderForTests = (): void => {
  if (import.meta.env.MODE !== 'test') {
    return;
  }
  koreanCatalogExpansionPromise = undefined;
};

export default i18n;
